/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createContext, FC, ReactNode, useContext, useState } from "react";
import { useLiveAPI, UseLiveAPIResults } from "../hooks/use-live-api";
import { LiveClientOptions } from "../types";

const VNA_TRAVEL_INFO_URL = "https://www.vietnamairlines.com/vn/vi/travel-information";

export type AppLanguage = "vi" | "en" | "nl";

export type LiveAPIContextValue = UseLiveAPIResults & {
  airportMapUrl: string | null;
  setAirportMapUrl: (url: string | null) => void;
  policyPageUrl: string | null;
  setPolicyPageUrl: (url: string | null) => void;
  showBookingInput: boolean;
  setShowBookingInput: (show: boolean) => void;
  language: AppLanguage;
  setLanguage: (lang: AppLanguage) => void;
};

export { VNA_TRAVEL_INFO_URL };

const LiveAPIContext = createContext<LiveAPIContextValue | undefined>(undefined);

export type LiveAPIProviderProps = {
  children: ReactNode;
  options: LiveClientOptions;
};

export const LiveAPIProvider: FC<LiveAPIProviderProps> = ({
  options,
  children,
}) => {
  const liveAPI = useLiveAPI(options);
  const [airportMapUrl, setAirportMapUrl] = useState<string | null>(null);
  const [policyPageUrl, setPolicyPageUrl] = useState<string | null>(null);
  const [showBookingInput, setShowBookingInput] = useState(false);
  const [language, setLanguage] = useState<AppLanguage>("vi");

  return (
    <LiveAPIContext.Provider
      value={{
        ...liveAPI,
        airportMapUrl,
        setAirportMapUrl,
        policyPageUrl,
        setPolicyPageUrl,
        showBookingInput,
        setShowBookingInput,
        language,
        setLanguage,
      }}
    >
      {children}
    </LiveAPIContext.Provider>
  );
};

export const useLiveAPIContext = (): LiveAPIContextValue => {
  const context = useContext(LiveAPIContext);
  if (!context) {
    throw new Error("useLiveAPIContext must be used within a LiveAPIProvider");
  }
  return context;
};
